<script setup lang="ts">
import { countryCodeToTaxSlug, type PayPeriod } from '~/utils/taxCalculatorUrl'

interface Props {
  stateSlug: string
  currentSalary?: number | null
  currentPeriod?: PayPeriod
  countryCode?: string
}

const props = withDefaults(defineProps<Props>(), {
  countryCode: 'US',
  currentPeriod: 'annually'
})

interface RelatedLink {
  path: string
  salary: number
  period: string
}

const COUNTRY_CURRENCY_MAP: Record<string, { currency: string; locale: string; symbol: string }> = {
  US: { currency: 'USD', locale: 'en-US', symbol: '$' },
  IN: { currency: 'INR', locale: 'en-IN', symbol: '₹' },
  DE: { currency: 'EUR', locale: 'de-DE', symbol: '€' },
  GB: { currency: 'GBP', locale: 'en-GB', symbol: '£' },
  CA: { currency: 'CAD', locale: 'en-CA', symbol: '$' }
}

const currencyConfig = computed(() => {
  return COUNTRY_CURRENCY_MAP[props.countryCode || 'US'] || COUNTRY_CURRENCY_MAP.US
})

const formatAmount = (amount: number): string => {
  const config = currencyConfig.value
  if (amount >= 1000) {
    const k = amount / 1000
    return `${config.symbol}${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`
  }
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    maximumFractionDigits: 0
  }).format(amount)
}

const countrySlug = computed(() => countryCodeToTaxSlug(props.countryCode) || 'usa')

const { data: relatedLinks, status } = await useFetch<{ success: boolean; data: RelatedLink[] }>(
  '/api/tax/related-salary-links',
  {
    query: computed(() => ({
      state: props.stateSlug,
      salary: props.currentSalary || undefined,
      period: props.currentPeriod || 'annually'
    })),
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] || nuxtApp.static.data[key]
  }
)

const links = computed(() => {
  if (!relatedLinks.value?.success || !relatedLinks.value.data) return []
  return relatedLinks.value.data.map(link => ({
    ...link,
    url: `/tax-calculator-${countrySlug.value}/${link.path}`,
    label: formatAmount(link.salary)
  }))
})

const isCurrentLink = (link: RelatedLink): boolean => {
  if (!props.currentSalary) return false
  return link.salary === props.currentSalary && link.period === props.currentPeriod
}
</script>

<template>
  <div v-if="links.length > 0" class="related-salary-links">
    <div class="flex flex-wrap gap-2">
      <NuxtLink
        v-for="link in links"
        :key="link.path"
        :to="link.url"
        class="px-3 py-1.5 text-sm rounded-lg transition-all duration-200"
        :class="isCurrentLink(link)
          ? 'bg-purple-600 text-white font-medium shadow-md'
          : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-700'"
      >
        {{ link.label }}
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped>
.related-salary-links {
  width: 100%;
}
</style>
